import { useState } from 'react';
import {
  Button,
  Dialog,
  Row,
  Sheet,
  Stack,
  ThemeToggle,
  ToastProvider,
  Tooltip,
  useToast,
} from '@fluent/ui';
import './Cluster4Review.css';

export function Cluster4Review() {
  return (
    <ToastProvider>
      <Cluster4Inner />
    </ToastProvider>
  );
}

function Cluster4Inner() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <main className="c4">
      <header className="c4__hero">
        <p className="c4__kicker">Phase 1 · Cluster 4</p>
        <h1>Layout &amp; overlay</h1>
        <p className="c4__lede">
          Structure (Screen, Container, Stack, Row) + overlays (Dialog, Sheet, Toast, Tooltip).
        </p>
        <div className="c4__theme-row">
          <ThemeToggle />
        </div>
      </header>

      {/* Overlays */}
      <section className="c4__section">
        <h2>Dialog &amp; Sheet</h2>
        <p className="c4__note">
          Dialog = centered modal (desktop feel). Sheet = bottom-anchored mobile modal. Both trap focus, close on ESC,
          close on backdrop click.
        </p>

        <Row gap={2} wrap>
          <Button variant="primary" onClick={() => setDialogOpen(true)}>
            გახსენი Dialog
          </Button>
          <Button variant="secondary" onClick={() => setSheetOpen(true)}>
            გახსენი Sheet
          </Button>
          <Button variant="danger" onClick={() => setConfirmOpen(true)}>
            წაშალე ანგარიში
          </Button>
        </Row>
      </section>

      {/* Toast */}
      <section className="c4__section">
        <h2>Toast</h2>
        <p className="c4__note">
          Small notifications that stack in the bottom-right. Auto-dismiss after 4s by default. 5 tones.
        </p>

        <Row gap={2} wrap>
          <Button variant="ghost" onClick={() => toast({ title: 'სანდო ინფო', tone: 'neutral' })}>
            Neutral
          </Button>
          <Button variant="primary" onClick={() => toast({ title: 'პროგრესი შენახულია', message: '+10 XP', tone: 'success' })}>
            Success
          </Button>
          <Button variant="neutral" onClick={() => toast({ title: 'ყურადღება', message: 'დღის მიზანი ჯერ არ გჭირდება', tone: 'warning' })}>
            Warning
          </Button>
          <Button variant="danger" onClick={() => toast({ title: 'კავშირი გაწყდა', message: 'სცადე ისევ', tone: 'danger' })}>
            Danger
          </Button>
          <Button variant="ghost" onClick={() => toast({ title: 'ახალი გაკვეთილი', message: 'ოჯახი 2/4', tone: 'info' })}>
            Info
          </Button>
        </Row>
      </section>

      {/* Tooltip */}
      <section className="c4__section">
        <h2>Tooltip</h2>
        <p className="c4__note">
          Hover (or focus) on any trigger to see its tooltip. On touch devices, tooltips don&apos;t auto-open — Radix
          hides them on touch-only.
        </p>

        <Row gap={3} wrap align="center">
          <Tooltip content="შემომოწმება შენი პასუხის" side="top">
            <Button variant="primary">Hover me</Button>
          </Tooltip>

          <Tooltip content="ამ ღილაკი საშიშია" side="right">
            <Button variant="danger">Danger</Button>
          </Tooltip>

          <Tooltip content="ძალიან გრძელი მინიშნება, რომელიც რამდენიმე ხაზს ეძებს მათი ჩვენებისთვის" side="bottom">
            <Button variant="neutral">Long text</Button>
          </Tooltip>
        </Row>
      </section>

      <footer className="c4__footer">
        <p>
          <b>Cluster 4 review.</b> Tell Jake: dialog animation, sheet slide-up feel, toast stacking, tooltip delay.
        </p>
      </footer>

      {/* Dialogs */}
      <Dialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        title="მოგესალმები!"
        description="ეს არის დიალოგის მაგალითი. შეიძლება დახურო ღილაკზე, ESC-ით, ან ფონზე დაჭერით."
        actions={
          <>
            <Button variant="neutral" onClick={() => setDialogOpen(false)}>
              გაუქმება
            </Button>
            <Button variant="primary" onClick={() => setDialogOpen(false)}>
              გასაგებია
            </Button>
          </>
        }
      >
        <p>
          ყოველი გაკვეთილი მთავრდება ამ ტიპის დიალოგით — საუკეთესო შედეგისთვის, ოქროს ნიშანი, თუ გაკვეთილი პირველ ცდაზე
          გაიარე სწორად.
        </p>
      </Dialog>

      <Sheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        title="პარამეტრები"
        description="სწრაფი პარამეტრების მაგალითი ფოთლად ქვემოდან."
        actions={
          <>
            <Button variant="neutral" onClick={() => setSheetOpen(false)}>
              დახურე
            </Button>
            <Button variant="primary" onClick={() => setSheetOpen(false)}>
              შენახვა
            </Button>
          </>
        }
      >
        <Stack gap={3}>
          <p>ქვედა ფურცელი — ტელეფონისთვის უკეთესი UX ვიდრე ცენტრალური დიალოგი.</p>
          <p>სრიალებს ზემოდან, დრეგ-ჰანდლი ჩანს, ფონზე ჩრდილი მუქდება.</p>
        </Stack>
      </Sheet>

      <Dialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="წაშალო ანგარიში?"
        description="ეს მოქმედება შეუქცევადია. ყველა შენი პროგრესი დაიკარგება."
        actions={
          <>
            <Button variant="neutral" onClick={() => setConfirmOpen(false)}>
              არა, დარჩე
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setConfirmOpen(false);
                toast({ title: 'ანგარიში წაშლილია', tone: 'danger' });
              }}
            >
              დიახ, წაშალე
            </Button>
          </>
        }
      />
    </main>
  );
}
